import { useRef, useEffect } from "react";

const VERTEX_SHADER = `
attribute vec2 a_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `
precision highp float;

uniform float u_time;
uniform vec2 u_res;
uniform float u_waveSpeed;
uniform float u_waveFreq;
uniform float u_waveHeight;
uniform float u_viscosity;
uniform float u_goldIntensity;

#define PI 3.14159265359

float hash(float n) {
  return fract(sin(n) * 43758.5453123);
}

float hash2(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash2(i);
  float b = hash2(i + vec2(1.0, 0.0));
  float c = hash2(i + vec2(0.0, 1.0));
  float d = hash2(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  vec2 shift = vec2(100.0);
  mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = rot * p * 2.0 + shift;
    a *= 0.5;
  }
  return v;
}

float waveColumn(vec2 uv, float t) {
  float visc = u_viscosity;
  float speed = u_waveSpeed;
  float freq = u_waveFreq;
  float height = u_waveHeight;
  float wave = sin(uv.x * freq + t * speed) * 0.5 + 0.5;
  wave = pow(wave, visc);
  float columnY = 0.5 + wave * height;
  float distY = uv.y - columnY;
  float colWidth = 0.12 + wave * 0.04;
  float body = smoothstep(colWidth, 0.0, abs(distY));
  float membrane = smoothstep(0.02, 0.0, distY);
  return max(body, membrane);
}

vec3 columnNormal(vec2 uv, float t) {
  float eps = 0.01;
  float center = waveColumn(uv, t);
  float left = waveColumn(uv - vec2(eps, 0.0), t);
  float right = waveColumn(uv + vec2(eps, 0.0), t);
  float up = waveColumn(uv + vec2(0.0, eps), t);
  float down = waveColumn(uv - vec2(0.0, eps), t);
  return normalize(vec3(left - right, down - up, 2.0 * eps));
}

float turbulence(vec2 uv, float t) {
  vec2 p1 = uv * 3.0 + vec2(t * 0.1, t * 0.08);
  vec2 p2 = uv * 5.0 - vec2(t * 0.15, t * 0.06);
  vec2 p3 = uv * 8.0 + vec2(t * 0.05, -t * 0.12);
  float n1 = fbm(p1);
  float n2 = fbm(p2);
  float n3 = fbm(p3);
  return n1 * 0.5 + n2 * 0.3 + n3 * 0.2;
}

float metallicSpecular(vec3 N, vec3 L, vec3 V, float roughness) {
  vec3 H = normalize(L + V);
  float NdotH = max(dot(N, H), 0.0);
  float spec = pow(NdotH, (1.0 - roughness) * 128.0);
  float fresnel = pow(1.0 - max(dot(N, V), 0.0), 5.0);
  return spec * (1.0 + fresnel * 2.0);
}

vec3 goldColor(float lum, float spec, float heat) {
  vec3 steelDark = vec3(0.02, 0.04, 0.08);
  vec3 steelBlue = vec3(0.04, 0.08, 0.16);
  vec3 bronze = vec3(0.35, 0.22, 0.08);
  vec3 brass = vec3(0.55, 0.45, 0.18);
  vec3 gold = vec3(0.78, 0.62, 0.24);
  vec3 brightGold = vec3(0.92, 0.78, 0.42);
  vec3 hotWhite = vec3(1.0, 0.96, 0.88);
  vec3 col = mix(steelDark, steelBlue, smoothstep(0.0, 0.05, lum));
  col = mix(col, bronze, smoothstep(0.04, 0.12, lum));
  col = mix(col, brass, smoothstep(0.08, 0.25, lum));
  col = mix(col, gold, smoothstep(0.15, 0.45, lum));
  col = mix(col, brightGold, smoothstep(0.35, 0.7, lum));
  col = mix(col, hotWhite, smoothstep(0.6, 1.0, lum));
  col += vec3(1.0, 0.9, 0.6) * spec * 1.5;
  col += vec3(1.0, 0.5, 0.1) * heat * 0.5;
  return col;
}

void main() {
  vec2 uv = (gl_FragCoord.xy - u_res * 0.5) / min(u_res.x, u_res.y);
  float t = u_time;
  vec3 col = vec3(0.0);

  float liq = waveColumn(uv, t);
  vec3 N = columnNormal(uv, t);

  float spec1 = metallicSpecular(N, normalize(vec3(0.3, 0.5, 1.0)), vec3(0.0, 0.0, 1.0), 0.3);
  float spec2 = metallicSpecular(N, normalize(vec3(-0.4, 0.3, 1.0)), vec3(0.0, 0.0, 1.0), 0.5);
  float spec3 = metallicSpecular(N, normalize(vec3(0.1, 0.8, 0.6)), vec3(0.0, 0.0, 1.0), 0.7);
  float spec = spec1 * 1.2 + spec2 * 0.6 + spec3 * 0.3;

  float amb = pow(1.0 - N.z, 3.0) * 0.3;

  float turb = turbulence(uv * vec2(2.0, 6.0) + vec2(0.0, t * 0.05), t * 0.3);
  float membrane = waveColumn(uv + vec2(0.0, 0.015), t) - waveColumn(uv - vec2(0.0, 0.01), t);
  float memDetail = max(membrane, 0.0) * (1.0 + turb * 0.5);

  float colLum = liq * 0.5 + spec + amb;
  float heat = smoothstep(0.5, 1.0, membrane) * 0.3;
  col = goldColor(colLum, spec, heat);

  float memBright = smoothstep(0.01, 0.08, memDetail);
  col += vec3(1.0, 0.9, 0.6) * memBright * 0.5;
  col += vec3(0.9, 0.7, 0.3) * turb * liq * 0.3;

  float edgeGlint = smoothstep(0.3, 0.9, metallicSpecular(N, normalize(vec3(0.6, 0.2, 1.0)), vec3(0.0, 0.0, 1.0), 0.2));
  col += vec3(1.0, 0.95, 0.7) * edgeGlint * 0.4;

  col *= u_goldIntensity;
  col = col / (1.0 + col * 0.3);
  col = pow(col, vec3(0.95, 0.98, 1.04));

  float vig = 1.0 - dot(uv * 0.8, uv * 0.8);
  col *= clamp(vig, 0.0, 1.0);

  gl_FragColor = vec4(col, 1.0);
}
`;

function createShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error("Shader compile error:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

export default function LiquidGoldCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      preserveDrawingBuffer: false,
    });
    if (!gl) return;

    const vertShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragShader = createShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertShader || !fragShader) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertShader);
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program link error:", gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    // Full-screen triangle
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    );

    const aPos = gl.getAttribLocation(program, "a_pos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    // Uniforms
    const uTime = gl.getUniformLocation(program, "u_time");
    const uRes = gl.getUniformLocation(program, "u_res");
    const uWaveSpeed = gl.getUniformLocation(program, "u_waveSpeed");
    const uWaveFreq = gl.getUniformLocation(program, "u_waveFreq");
    const uWaveHeight = gl.getUniformLocation(program, "u_waveHeight");
    const uViscosity = gl.getUniformLocation(program, "u_viscosity");
    const uGoldIntensity = gl.getUniformLocation(program, "u_goldIntensity");

    gl.uniform1f(uWaveSpeed, 1.0);
    gl.uniform1f(uWaveFreq, 4.0);
    gl.uniform1f(uWaveHeight, 0.25);
    gl.uniform1f(uViscosity, 2.5);
    gl.uniform1f(uGoldIntensity, 1.1);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uRes, canvas.width, canvas.height);
    };

    resize();
    window.addEventListener("resize", resize);

    let rafId: number;
    const render = () => {
      gl.uniform1f(uTime, performance.now() * 0.001);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      rafId = requestAnimationFrame(render);
    };
    rafId = requestAnimationFrame(render);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafId);
      gl.deleteProgram(program);
      gl.deleteShader(vertShader);
      gl.deleteShader(fragShader);
      gl.deleteBuffer(buffer);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
      }}
    />
  );
}
